import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Helmet } from 'react-helmet-async'
import { getLetsJerktvVideoDetails, parseVideoId, extractIdFromUrl } from '@/services/adultContent'
import './ContentPage.css'

interface LetsJerktvRelatedVideo {
  title: string
  video_link: string
  thumbnail: string
  rating: string
  quality: string
  duration: string
}

interface LetsJerktvVideoDetails {
  title: string
  iframe_src: string
  embed_url: string
  download_url?: string
  thumbnail: string
  tags: string[]
  categories: string[]
  related_videos: LetsJerktvRelatedVideo[]
}

const LetsJerktvContentPage = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const parsedId = id ? parseVideoId(id) : null

  // Construire l'URL complète depuis le slug
  const videoUrl = parsedId?.videoId 
    ? `https://letsjerk.tv/${parsedId.videoId}/`
    : null

  // Requête pour récupérer les détails de la vidéo
  const { data: videoData, isLoading, error } = useQuery({
    queryKey: ['letsjerktv-video', parsedId?.videoId],
    queryFn: async () => {
      if (!videoUrl) throw new Error('URL de vidéo manquante')
      return await getLetsJerktvVideoDetails(videoUrl) as LetsJerktvVideoDetails
    },
    enabled: !!videoUrl && !!parsedId,
  })

  // Fonction pour formater la durée
  const formatDuration = (duration?: string): string => {
    if (!duration) return ''
    return duration // Format déjà "MM:SS"
  }

  // Fonction pour extraire le slug depuis video_link
  const getVideoSlug = (videoLink: string): string => {
    const slug = extractIdFromUrl(videoLink)
    return slug || videoLink.split('/').filter(Boolean).pop() || ''
  }

  // Navigation vers une vidéo liée
  const handleRelatedVideoClick = (videoLink: string) => {
    const slug = getVideoSlug(videoLink)
    if (slug) {
      navigate(`/content/letsjerktv_${slug}`)
    }
  }

  if (isLoading) {
    return <div className="content-page"><div className="content-page-loading">Chargement...</div></div>
  }

  if (error || !videoData) {
    return <div className="content-page"><div className="content-page-error">Contenu non trouvé</div></div>
  }

  const location = useLocation()
  const pageTitle = `${videoData.title} - LetsJerktv - EdenPlaisir.com`
  const pageDescription = `Regardez ${videoData.title} sur EdenPlaisir.com`
  const canonicalUrl = `https://www.edenplaisir.com${location.pathname}`.replace(/\/$/, '').split('?')[0] || `https://www.edenplaisir.com/content/letsjerktv/${id}`
  
  return (
    <div className="content-page">
      <Helmet>
        <title>{pageTitle}</title>
        <link rel="canonical" href={canonicalUrl} />
        <meta name="description" content={pageDescription} />
        <meta name="robots" content="noindex, follow" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="video.movie" />
        <meta property="og:url" content={canonicalUrl} />
        <meta name="twitter:card" content="player" />
      </Helmet>
      
      <div className="content-page-header">
        <h1 className="content-page-title">{videoData.title}</h1>
      </div>

      {/* Layout principal avec vidéo et sidebar */}
      <div className="content-page-main">
        <div className="content-page-main-content">
          {/* Affichage de la vidéo */}
          {videoData.iframe_src && (
            <div className="content-page-video-container">
              <iframe
                src={videoData.iframe_src}
                width="100%"
                height="100%"
                allowFullScreen
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                title={videoData.title}
              />
            </div>
          )}
        </div>

        <div className="content-page-sidebar">
          {/* Catégories */}
          {videoData.categories && videoData.categories.length > 0 && (
            <div className="content-page-categories">
              <h3>Catégories</h3>
              <div className="content-page-category-list">
                {videoData.categories.map((category, index) => (
                  <span key={index} className="content-page-category">{category}</span>
                ))}
              </div>
            </div>
          )}

          {/* Tags */}
          {videoData.tags && videoData.tags.length > 0 && (
            <div className="content-page-tags">
              <h3>Tags</h3>
              <div className="content-page-tag-list">
                {videoData.tags.map((tag, index) => (
                  <span 
                    key={index} 
                    className="content-page-tag clickable"
                    onClick={() => navigate('/categories', { state: { filterTag: tag } })}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Vidéos liées */}
      {videoData.related_videos && videoData.related_videos.length > 0 && (
        <div className="content-page-related">
          <h2 className="content-page-section-title">Vidéos similaires</h2>
          <div className="content-page-related-grid">
            {videoData.related_videos.slice(0, 12).map((relatedVideo, index) => (
              <div
                key={index}
                className="content-page-related-card"
                onClick={() => handleRelatedVideoClick(relatedVideo.video_link)}
                style={{ cursor: 'pointer' }}
              >
                <div className="content-page-related-thumbnail">
                  <img
                    src={relatedVideo.thumbnail}
                    alt={relatedVideo.title}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement
                      target.style.display = 'none'
                    }}
                  />
                  <div className="content-page-related-duration">
                    {formatDuration(relatedVideo.duration)}
                  </div>
                  {relatedVideo.quality && (
                    <div className="content-page-related-quality">
                      {relatedVideo.quality}
                    </div>
                  )}
                </div>
                <div className="content-page-related-info">
                  <h4 className="content-page-related-title">{relatedVideo.title}</h4>
                  {relatedVideo.rating && (
                    <div className="content-page-related-rating">
                      ⭐ {relatedVideo.rating}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default LetsJerktvContentPage

